import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';

import {
  constants,
  SiteConfigManager,
  useApplicationState,
  useDetection,
  usePaymentMethods,
} from '@atlas/core';
import { Button, Icon, Label, Loader, MoneyInput } from '@atlas/ui';

import QuickDepositPaymentMethod from 'src/common/Popup/QuickDepositPopup/QuickDepositPaymentMethod';
import { useSignupPopup } from 'src/components/Auth/useSignUpPopup';
import { useBonusesContext } from 'src/components/BonusesProvider';
import useFormatter from 'src/hooks/useFormatter';

import { useDepositSubmit } from './hooks/useDepositSubmit';
import { useInitialPayment } from './hooks/useInitialPayment';
import QuickDepositBonuses from './QuickDepositBonuses';
import QuickDepositPaymentMethodDropdown from './QuickDepositPaymentMethodDropdown';

export const BUTTON_POSITION = {
  BOTTOM: 'bottom',
  INLINE: 'inline',
};

const QuickDepositForm = ({
  buttonPosition = BUTTON_POSITION.INLINE,
  closeQRCodeOnSuccess,
  onClose,
  onDepositLoaderShown,
  onDepositRedirectSuccess,
  onDepositStart,
  onDepositSuccess,
  setPaymentUrl,
  setRedirectUrl,
}) => {
  const {
    wallet: { currency },
  } = useApplicationState();
  const history = useHistory();

  const { currencyFormatter } = useFormatter();
  const { paymentMethods } = usePaymentMethods(
    constants.PAYMENT_OPERATION.DEPOSIT,
    constants.DEVICE_TYPES.MOBILE,
  );

  const { browser, ip, locale, os } = useDetection();
  const showInPopup = SiteConfigManager.getConfig('config.config.registration.showInPopup', false);
  const { openSignupPopup } = useSignupPopup();

  const [isInputActive, setIsInputActive] = useState(false);
  const [value, setValue] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { initialValue, paymentMethod, setPaymentMethod } = useInitialPayment(
    paymentMethods,
    currency,
  );
  const {
    amountToGetBonus,
    bonusesList,
    setDepositAmount,
    setPaymentMethod: setBonusesPaymentMethod,
  } = useBonusesContext();
  const hasSingleBonus = bonusesList.length === 1;

  useEffect(() => {
    if (initialValue && value === '') {
      setValue(initialValue);
      setDepositAmount(initialValue);
    }
  }, [initialValue, setDepositAmount, value]);

  const { error, handleDeposit, loading, setError } = useDepositSubmit({
    browser,
    closeQRCodeOnSuccess,
    history,
    ip,
    locale,
    onClose,
    onDepositLoaderShown,
    onDepositRedirectSuccess,
    onDepositStart,
    onDepositSuccess,
    openSignupPopup,
    os,
    paymentMethod,
    setPaymentUrl,
    setRedirectUrl,
    showInPopup,
    value,
  });

  const clampValueToLimits = useCallback(() => {
    if (!paymentMethod || !value) return;
    const numValue = Number(value);

    if (paymentMethod.maxAmount && numValue > paymentMethod.maxAmount) {
      setValue(paymentMethod.maxAmount);
      setDepositAmount(paymentMethod.maxAmount);
      setError(null);
    } else if (paymentMethod.minAmount && numValue < paymentMethod.minAmount) {
      setValue(paymentMethod.minAmount);
      setDepositAmount(paymentMethod.minAmount);
      setError(null);
    }
  }, [paymentMethod, setDepositAmount, setError, value]);

  const prevPaymentMethodRef = useRef(paymentMethod);

  useEffect(() => {
    if (prevPaymentMethodRef.current === paymentMethod) return;
    prevPaymentMethodRef.current = paymentMethod;
    clampValueToLimits();
  }, [paymentMethod, clampValueToLimits]);

  const disableButton = !value || loading;

  const handleValueChange = value => {
    setValue(value);
    setDepositAmount(value);

    if (error) {
      setError(null);
    }
  };

  const handleBlur = useCallback(() => {
    setIsInputActive(false);
    clampValueToLimits();
  }, [clampValueToLimits]);

  const handleSavePaymentMethod = useCallback(
    paymentMethod => {
      setPaymentMethod(paymentMethod);
      setBonusesPaymentMethod(paymentMethod);
      setIsDropdownOpen(false);
    },
    [setPaymentMethod, setBonusesPaymentMethod],
  );

  useEffect(() => {
    if (paymentMethod) {
      setBonusesPaymentMethod(paymentMethod);
    }
  }, [paymentMethod, setBonusesPaymentMethod]);

  return (
    <div className="quick-deposit-content">
      {paymentMethod ? (
        <>
          <div className="quick-deposit-form">
            {isDropdownOpen && paymentMethods.length > 1 && (
              <QuickDepositPaymentMethodDropdown
                onSelect={handleSavePaymentMethod}
                paymentMethod={paymentMethod}
                paymentMethods={paymentMethods}
              />
            )}
            <div className="quick-deposit-input">
              <QuickDepositPaymentMethod
                isDropdownOpen={isDropdownOpen}
                paymentMethod={paymentMethod}
                setIsDropdownOpen={setIsDropdownOpen}
                withDropdown={paymentMethods.length > 1}
              />
              <MoneyInput
                active={isInputActive}
                currency={currency}
                error={error}
                label="amount"
                onBlur={handleBlur}
                onChange={handleValueChange}
                onFocus={() => setIsInputActive(true)}
                value={value}
              />
              {buttonPosition === BUTTON_POSITION.INLINE && (
                <Button
                  className="deposit-button"
                  disabled={disableButton}
                  label="deposit"
                  loading={loading}
                  onClick={handleDeposit}
                  size="big"
                />
              )}
            </div>
            {error && (
              <div className="quick-deposit-form-error">
                <Icon className="error-icon" height={18} name="error" width={18} />
                <Label className="error-message" message={error.message} />
              </div>
            )}
          </div>
          {amountToGetBonus > 0 && (
            <Label
              className="quick-deposit-bonus-info"
              message="quick_deposit_add_amount_to_get_bonus"
              params={{
                amount: currencyFormatter.format(amountToGetBonus),
              }}
            />
          )}
          {hasSingleBonus && (
            <Label
              className="quick-deposit-get-bouns-title"
              message="insufficient_funds_get_bonus"
            />
          )}

          <QuickDepositBonuses />
          {buttonPosition === BUTTON_POSITION.BOTTOM && (
            <Button
              className="deposit-button"
              disabled={disableButton}
              label="deposit"
              loading={loading}
              onClick={handleDeposit}
              size="big"
            />
          )}
        </>
      ) : (
        <Loader />
      )}
    </div>
  );
};

QuickDepositForm.propTypes = {
  buttonPosition: PropTypes.oneOf(['inline', 'bottom']),
  closeQRCodeOnSuccess: PropTypes.bool,
  onClose: PropTypes.func,
  onDepositLoaderShown: PropTypes.func,
  onDepositRedirectSuccess: PropTypes.func,
  onDepositStart: PropTypes.func,
  onDepositSuccess: PropTypes.func,
  setPaymentUrl: PropTypes.func.isRequired,
  setRedirectUrl: PropTypes.func,
};

export default QuickDepositForm;
